'use client';

import styled from 'styled-components';
import { useFeatureFlagEnabled } from '@frameio/feature-flags';
import type { InboxAccount } from '@frameio/graphql-client/generated';
import { AvatarHalo } from '@frameio/shared/components/AvatarEditor';
import type { AvatarSizes } from '@frameio/vapor';
import { Avatar, Badge, getAvatarColor, Text, theme } from '@frameio/vapor';
import { IconChevronDown } from '@frameio/vapor-icons';
import { PRIMARY_NAV_COLLAPSED_WIDTH } from '../../constants';
import { useWhatsNew } from '../AppSheet/Sheets/WhatsNewSheet/WhatsNewContext';
import { AccountSwitcherMenu } from './AccountSwitcherMenu';
import { AVATAR_SIZE } from './constants';
import type { onAccountChangedFn } from './types';

const avatarHaloStyle = {
  filter: 'blur(16px)',
  opacity: 0.7,
  transform: 'scale(3.8) translate3d(0, 4px, 0)',
} as const;

type AccountForAvatar = {
  id: string;
  displayName?: string;
  imageUrl?: string;
};

export const AccountAvatar = <Account extends AccountForAvatar>(props: {
  account: Account | InboxAccount;
  minimal?: boolean;
  onAccountChanged: onAccountChangedFn;
  skipFadeIn?: boolean;
  size?: AvatarSizes;
  showHalo?: boolean;
}) => {
  const isGlobalNavEnabled = useFeatureFlagEnabled('v4.global_navigation');
  const accountId = props.account?.id;
  const displayName = props.account?.displayName;
  const avatarImage = props.account?.imageUrl;
  const size = props.size;
  const showHalo = props.showHalo ?? true;
  const fallbackColor = avatarImage
    ? 'transparent'
    : getAvatarColor(displayName || '');
  const { hasUpdatesSinceLastViewed } = useWhatsNew();

  if (isGlobalNavEnabled) {
    return (
      <>
        {showHalo && (
          <AvatarHaloWrapper>
            <AvatarHalo
              alt={displayName || ''}
              fallback={fallbackColor}
              shape="square"
              size={size ?? AVATAR_SIZE}
              src={avatarImage ?? undefined}
              style={avatarHaloStyle}
              transitionDelaySeconds={0}
              transitionDurationSeconds={props.skipFadeIn ? 0 : undefined}
            />
          </AvatarHaloWrapper>
        )}
        <AccountSwitcherMenu
          activeAccountId={accountId ?? ''}
          minimal={props.minimal}
          onAccountChanged={props.onAccountChanged}
        >
          <AvatarButton data-dd-action-name="primary-nav.account-menu">
            <Badge
              backgroundColor="error"
              borderColor={theme.colors.components.sheet.background}
              borderSize={2}
              data-testid="whats-new-button-badge"
              isShowing={hasUpdatesSinceLastViewed()}
              label="label"
              placement="top-right"
            >
              <Avatar
                key={`account-switcher-avatar-${accountId}`}
                alt={displayName ?? ''}
                background={displayName ? undefined : 'transparent'}
                data-testid="account-avatar"
                shape="square"
                shouldFadeOnLoad={false}
                size={size ?? AVATAR_SIZE}
                src={avatarImage ?? undefined}
              />
            </Badge>
            <AvatarLabel
              enableTooltip={true}
              fontWeight="medium"
              truncate={true}
              variant="body"
            >
              {props.account?.displayName ?? ''}
            </AvatarLabel>
            <IconChevronDown size={16} />
          </AvatarButton>
        </AccountSwitcherMenu>
      </>
    );
  }

  return (
    <>
      {showHalo && (
        <AvatarHaloWrapper>
          <AvatarHalo
            alt={displayName || ''}
            fallback={fallbackColor}
            shape="square"
            size={size ?? AVATAR_SIZE}
            src={avatarImage ?? undefined}
            style={avatarHaloStyle}
            transitionDelaySeconds={0}
            transitionDurationSeconds={props.skipFadeIn ? 0 : undefined}
          />
        </AvatarHaloWrapper>
      )}
      <AccountSwitcherMenu
        activeAccountId={accountId ?? ''}
        minimal={props.minimal}
        onAccountChanged={props.onAccountChanged}
      >
        <LegacyAvatarButton data-dd-action-name="primary-nav.account-menu">
          <Avatar
            key={`account-switcher-avatar-${accountId}`}
            alt={displayName ?? ''}
            background={displayName ? undefined : 'transparent'}
            data-testid="account-avatar"
            shape="square"
            shouldFadeOnLoad={false}
            size={size ?? AVATAR_SIZE}
            src={avatarImage ?? undefined}
          />
        </LegacyAvatarButton>
      </AccountSwitcherMenu>
    </>
  );
};

const AvatarHaloWrapper = styled.div`
  align-items: center;
  bottom: 0;
  box-sizing: content-box;
  display: flex;
  height: 180px;
  justify-content: center;
  overflow: hidden;
  pointer-events: none;
  position: absolute;
  width: 100%;
`;

const LegacyAvatarButton = styled.button`
  background: inherit;
  border: none;
  cursor: pointer;
  outline: none;
`;

const AvatarButton = styled.button`
  align-items: center;
  background: inherit;
  border: none;
  cursor: pointer;
  display: flex;
  gap: ${theme.spaces[8]};
  outline: none;
`;

const AvatarLabel = styled(Text)`
  opacity: 0;
  transition: opacity ${theme.transitions.durations[250]}
    ${theme.transitions.eases.easeOut};

  @container primary-nav-button-group (min-width: ${PRIMARY_NAV_COLLAPSED_WIDTH}px) {
    opacity: 1;
  }
`;
